import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getTranslatedField } from '@/lib/translationHelpers';
import { FlagIcon } from '@/components/FlagIcon';
import type { Product } from '@/hooks/useProducts';

const countryToFlag: Record<string, string> = { SE: 'sv', NO: 'no', DK: 'da', FI: 'fi' };

interface ProductListCardProps {
  product: Product;
  categoryIcon: React.ElementType;
  categoryLabel: string;
  lang: string;
  formatPrice: (price: number, currency: string) => string;
  updateFrequencyLabels: Record<string, string>;
}

export default function ProductListCard({
  product,
  categoryIcon: CategoryIcon,
  categoryLabel,
  lang,
  formatPrice,
  updateFrequencyLabels,
}: ProductListCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
        {/* Left: icon + name + SKU */}
        <div className="flex items-start gap-3 md:w-[280px] md:flex-shrink-0">
          <div className="w-8 h-8 rounded-md bg-secondary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <CategoryIcon className="w-4 h-4 text-secondary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm leading-tight line-clamp-2">
              {getTranslatedField(product, 'name', lang)}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {categoryLabel}
              </Badge>
              {product.sku && (
                <Badge variant="outline" className="text-xs font-mono">
                  {product.sku}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Middle: description + specs */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2 md:mb-0">
            {getTranslatedField(product, 'short_description', lang)}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
            {product.file_format && product.file_format.length > 0 && (
              <span>
                <span className="font-medium text-foreground">{t('shop.format')}:</span>{' '}
                {product.file_format.join(', ')}
              </span>
            )}
            {product.update_frequency && (
              <span>
                <span className="font-medium text-foreground">{t('shop.dataUpdatedFrequency')}:</span>{' '}
                {t(updateFrequencyLabels[product.update_frequency])}
              </span>
            )}
            {product.row_count && (
              <span>
                <span className="font-medium text-foreground">{t('dataFiles.specs.rows')}:</span>{' '}
                {product.row_count}
              </span>
            )}
          </div>
        </div>

        {/* Country flags + price + button */}
        <div className="flex items-center gap-4 md:flex-shrink-0 md:ml-auto">
          {product.available_countries && product.available_countries.length > 0 && (
            <div className="flex items-center gap-1">
              {product.available_countries.map((c) => {
                const flag = countryToFlag[c];
                return flag ? <FlagIcon key={c} code={flag} className="w-5 h-3.5" /> : null;
              })}
            </div>
          )}
          <div className="text-right">
            <p className="text-lg font-bold text-foreground whitespace-nowrap">
              {formatPrice(product.price, product.currency)}
            </p>
            <p className="text-xs text-muted-foreground">{t('shop.exVat')}</p>
          </div>
          <Button asChild size="sm">
            <Link to={`/${lang}/shop/${product.slug}`}>
              {t('common.readMore')}
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
